import React, { KeyboardEvent, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import * as yup from 'yup';
import { MarginProps } from '../../../../../theme/styledProps';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { Select } from '../../../../../CommonComponents/OPL/Select/Select/Select';
import { HelperText } from '../../../../../CommonComponents/OPL/HelperText/HelperText';
import { EMAILS_OPTIONS, FIELD_NAME } from './SharingPlansEmails.meta';
import { FormValues } from '../../MySettings.meta';
import { Modal } from '../../../../../CommonComponents/OPL/Modal/Modal';
import { ModalHeader } from '../../../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalBody } from '../../../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalFooter } from '../../../../../CommonComponents/OPL/Modal/ModalFooter';
import { Button } from '../../../../../CommonComponents/OPL/Button/Button';
import { ModalCloseButton } from '../../../../../CommonComponents/OPL/Modal/ModalCloseButton';

interface Props extends MarginProps {
  formBag: UseFormReturn<FormValues>;
}

type EmailListChanged =
  | {
      type: 'add';
      emailList: string;
    }
  | {
      type: 'remove';
      emailList: string[];
    };

export const SharingPlansEmails = ({ formBag, ...rest }: Props) => {
  const [inputValue, setInputValue] = useState('');
  const [innerError, setInnerError] = useState<string | null>(null);
  const [emailListChanged, setEmailListChanged] = useState<EmailListChanged | null>(null);

  const emails = formBag.watch(FIELD_NAME);

  const addEmail = () => {
    if (emailListChanged) {
      return;
    }

    // Do not add the same email more than once
    if (emails?.find(email => email === inputValue)) {
      setInnerError('Choose a different email');

      setTimeout(() => setInnerError(null), 2000);
    } else {
      setEmailListChanged({
        type: 'add',
        emailList: inputValue,
      });
    }
  };

  const handleChange = (opts: EMAILS_OPTIONS) => {
    const emailList = opts.map(({ value: email }) => email);

    // Only open confirmation when removing items
    if ((emails || []).length > emailList.length && !emailListChanged) {
      setEmailListChanged({
        type: 'remove',
        emailList,
      });

      return;
    }

    formBag.setValue(FIELD_NAME, emailList, { shouldDirty: true });
  };

  const handleInputChange = (newInputValue: string) => {
    setInputValue(newInputValue);
  };

  const handleKeyDown = (evt: KeyboardEvent<HTMLElement>) => {
    if (!inputValue) {
      return;
    }

    if (['Enter', 'Tab', ' ', ','].includes(evt.key)) {
      evt.stopPropagation();
      evt.preventDefault();

      addEmail();
    }
  };

  const handleBlur = () => {
    if (!inputValue) {
      return;
    }

    addEmail();
  };

  return (
    <Box as="section" {...rest}>
      <Box display="flex" alignItems="center" mb="4">
        <Text variant="formLabel">
          Email Addresses of Users that all of your plans will automatically be shared with
        </Text>
      </Box>

      <Controller
        control={formBag.control}
        name={FIELD_NAME}
        render={({ field: { value } }) => {
          return (
            <>
              <Select
                isMulti
                isClearable={false}
                aria-label="Share Email Addresses"
                value={(value || []).map(email => ({
                  value: email,
                  label: email,
                  isInvalid: !yup
                    .string()
                    .email()
                    .isValidSync(email),
                }))}
                inputValue={inputValue}
                components={{
                  DropdownIndicator: null,
                }}
                menuIsOpen={false}
                onChange={opts => {
                  handleChange((opts as unknown) as EMAILS_OPTIONS);
                }}
                onInputChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                placeholder="Type an email and press enter..."
              />

              {innerError && (
                <HelperText variant="error" mt="4">
                  {innerError}
                </HelperText>
              )}

              {formBag.formState.errors[FIELD_NAME] && (
                <HelperText variant="error" mt="4">
                  Please enter only valid email addresses
                </HelperText>
              )}
            </>
          );
        }}
      />

      {emailListChanged && (
        <Modal
          isOpen
          onRequestClose={() => setEmailListChanged(null)}
          aria={{ labelledby: 'sharing-email-modal' }}
        >
          <ModalHeader id="sharing-email-modal" variant="warning">
            Warning
          </ModalHeader>
          <ModalCloseButton onClick={() => setEmailListChanged(null)} />

          <ModalBody>
            {emailListChanged.type === 'add' ? (
              <Text>
                You are adding this person to have access to all of your existing and future plans.
                If you would like to remove them from a particular plan, you can still do so by
                going to the client's Sharing Settings.
              </Text>
            ) : (
              <Text>
                By removing this person, you are removing them from having access to all of your
                plans. You can still allow them to access individual plans by going to the client's
                Sharing Settings.
              </Text>
            )}
          </ModalBody>

          <ModalFooter>
            <Button variant="subtleInactive" onClick={() => setEmailListChanged(null)}>
              Cancel
            </Button>

            <Button
              ml="8"
              onClick={() => {
                if (emailListChanged.type === 'add') {
                  formBag.setValue(FIELD_NAME, [...(emails || []), emailListChanged.emailList], {
                    shouldDirty: true,
                  });
                  setInputValue('');
                } else {
                  formBag.setValue(FIELD_NAME, emailListChanged.emailList, { shouldDirty: true });
                }

                setEmailListChanged(null);
              }}
            >
              Agree
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </Box>
  );
};
